import PropTypes from 'prop-types';
import React, { useState, useRef } from 'react';
import { useQuery } from '@apollo/client';
import EmailUsEntryModal from './EmailUsEntryModal';
import EmailUsHelpDirections from './EmailUsHelpDirections';
import EmailUsContainerV2 from './EmailUsContainerV2';
import { EMAIL_US_FORM_TEXT_QUERY } from './emailUsOperations';
import { LOADING_MESSAGE, ERROR_MESSAGE } from '../../Messages/Messages';
import { validateWithReason } from './emailUsValidation';

export const HELP_REASONS = {
  ORDER_STATUS: 'Order Status',
  CANCEL_CHANGE_ORDER: 'Cancel/Change Order',
  RETURNS_EXCHANGES: 'Returns/Exchanges',
};

export default function EmailUsV2Wrapper({ onSuccess }) {
  const [step, setStep] = useState('entry'); // 'entry', 'directions', 'form'
  const [selectedReason, setSelectedReason] = useState('');
  const [selectedReasonKey, setSelectedReasonKey] = useState('');
  const formRef = useRef(null);
  const [formIsInvalid, setFormIsInvalid] = useState(false);
  const [invalidFields, setInvalidFields] = useState([]);
  const [continueToEmailModalOpen, setContinueToEmailModalOpen] = useState(false);

  const { data, loading, error } = useQuery(EMAIL_US_FORM_TEXT_QUERY);

  if (loading) return LOADING_MESSAGE;
  if (error) return ERROR_MESSAGE;

  const { textFor: text = {}, featureConfig = {} } = data || {};

  const handleOpenEmailClient = () => {
    const CUSTOMER_SERVICE_EMAIL = featureConfig?.gladlyCustomerServiceEmail;
    const formData = new FormData(formRef.current);
    const values = Object.fromEntries(formData.entries());

    // Build subject: Contact Reason Order Number
    const subject = encodeURIComponent(
      `${selectedReason} ${values.orderNum}`.trim(),
    );

    // Body includes structured information
    const body = encodeURIComponent(
      `${values.firstName} ${values.lastName}\n${selectedReason}\n${values.comment}`,
    );

    // Replace with your support email address
    const mailto = `mailto:${CUSTOMER_SERVICE_EMAIL}?subject=${subject}&body=${body}`;

    window.open(mailto, '_self');

    // Close modal after opening email client
    onSuccess();
  };

  /**
   * Maps help reason constants to their corresponding direction text.
   *
   * @type {Object.<string, string>}
   * @property {string} [HELP_REASONS.ORDER_STATUS] - Directions for order status inquiries.
   * @property {string} [HELP_REASONS.CANCEL_CHANGE_ORDER] - Directions for order cancellation or modification requests.
   * @property {string} [HELP_REASONS.RETURNS_EXCHANGES] - Directions for return or exchange requests.
   */
  const directionsMap = {
    [HELP_REASONS.ORDER_STATUS]: text.formOrderShipArriveDirections,
    [HELP_REASONS.CANCEL_CHANGE_ORDER]: text.formCancelORChangeOrderDirections,
    [HELP_REASONS.RETURNS_EXCHANGES]: text.formReturnExchangeDirections,
  };

  /**
   * Checks if the given reason key has associated directions.
   *
   * @param {string} reasonKey - The key representing the reason to check.
   * @returns {boolean} True if directions exist for the given reason key, otherwise false.
   */
  const reasonHasDirections = (reasonKey) => Boolean(directionsMap[reasonKey]);

  /**
   * Updates the current form step based on the provided reason or the currently selected reason.
   * If the current step is 'form' and the reason has directions, sets the step to 'entry'.
   * If the reason has directions (but not currently on 'form'), sets the step to 'directions'.
   * Otherwise, sets the step to 'form'.
   *
   * @param {string} [reason=selectedReason] - The reason to evaluate for determining the next step.
   */
  const setFormStep = (reason = selectedReason) => {
    const hasDirections = reasonHasDirections(reason);

    if (step === 'form' && hasDirections) {
      setStep('entry');
    } else if (hasDirections) {
      setStep('directions');
    } else {
      setStep('form');
    }
  };

  /**
   * Handles the change event for the help reason selection.
   * Updates the selected reason state and, if not on the 'entry' step,
   * advances the form step to the newly selected reason.
   *
   * @param {React.ChangeEvent<HTMLInputElement | HTMLSelectElement>} e - The change event triggered by selecting a new help reason.
   */
  const handleHelpReasonChange = (e, reasonKey) => {
    const newReason = e.target.value;
    setSelectedReason(newReason);
    setSelectedReasonKey(reasonKey);

    if (step !== 'entry') {
      setFormStep(newReason);
    }
  };

  /**
   * Handles the "Continue" action in the form workflow.
   * Validates the form using the provided form reference and checks if the 'contactReason' field is valid.
   * If invalid, sets the form as invalid and prevents further progression.
   * If valid, clears the invalid state and advances the form step.
   */
  const handleOnContinue = () => {
    const contactReasonIsInvalid = validateWithReason(formRef, selectedReason).includes('contactReason');

    if (contactReasonIsInvalid) {
      setFormIsInvalid(true);
      return;
    }

    setFormIsInvalid(false);
    setFormStep();
  };

  const handleOnSubmit = async () => {
    const invalidList = validateWithReason(formRef, selectedReason);
    setInvalidFields(invalidList);

    const isValid = invalidList.length === 0;
    setFormIsInvalid(!isValid);

    if (isValid) {
      setContinueToEmailModalOpen(true);
    }
  };

  const handleModalClose = () => { setContinueToEmailModalOpen(false); };

  /**
   * Determines whether the entry modal should be displayed based on the current step
   * and whether the selected reason requires additional directions.
   *
   * @type {boolean}
   * @param {string} step - The current step in the email process ('entry', 'directions', etc.).
   * @param {string} selectedReason - The reason selected by the user.
   * @returns {boolean} True if the entry modal should be shown; otherwise, false.
   */
  const shouldShowEntryModal = step === 'entry' || (step === 'directions' && reasonHasDirections(selectedReason));

  // Render logic
  if (shouldShowEntryModal) {
    return (
      <EmailUsEntryModal
        formRef={formRef}
        onSuccess={onSuccess}
        handleHelpReasonChange={handleHelpReasonChange}
        handleOnContinue={handleOnContinue}
        selectedReason={selectedReason}
        selectedReasonKey={selectedReasonKey}
        contactReasonIsInvalid={formIsInvalid}
        text={text}
        step={step}
      >
        {step === 'directions' && (
          <EmailUsHelpDirections
            text={directionsMap[selectedReason]}
          />
        )}
      </EmailUsEntryModal>
    );
  }

  if (step === 'form') {
    return (
      <EmailUsContainerV2
        formRef={formRef}
        formIsInvalid={formIsInvalid}
        invalidFields={invalidFields}
        selectedReason={selectedReason}
        selectedReasonKey={selectedReasonKey}
        setSelectedReason={setSelectedReason}
        setFormStep={setFormStep}
        handleHelpReasonChange={handleHelpReasonChange}
        handleOnSubmit={handleOnSubmit}
        handleModalClose={handleModalClose}
        text={text}
        isOpen={continueToEmailModalOpen}
        handleOpenEmailClient={handleOpenEmailClient}
      />
    );
  }
}

const propTypes = {
  onSuccess: PropTypes.func.isRequired,
};

EmailUsV2Wrapper.propTypes = propTypes;
